// Э-8. План поставок: «клиент/объект × месяцы» с правкой в ячейке, факт рядом с планом, сдвиг плана объекта,
// импорт из Excel. Правка к ТЗ: «заявлено клиентом» и «принято в план» — две цифры.

import { useRef, useState } from "react";
import { fmtNum } from "../../shared/format";
import { setQuery, useRoute } from "../../shared/router";
import { TopBar } from "../../shared/shell";
import { Button, Field, Input, Loading, Modal, Select, useAsync, useToast } from "../../shared/ui";
import { api, can, useCtx } from "../ctx";

const M = ["янв", "фев", "мар", "апр", "май", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];

export function PlanPage() {
  const { me } = useCtx();
  const toast = useToast();
  const r = useRoute();
  const year = Number(r.query.get("year") || new Date().getFullYear());
  const m = useAsync<any>(() => api.get("/supply-plans", { year }), [year]);
  const [edit, setEdit] = useState<{ row: any; month: number } | null>(null);
  const [shift, setShift] = useState<any | null>(null);
  const [adding, setAdding] = useState(false);
  const file = useRef<HTMLInputElement>(null);
  const editable = can.plan(me.role);

  const importFile = async (f: File) => {
    const fd = new FormData();
    fd.append("file", f);
    try {
      const res = await api.raw<any>("POST", `/supply-plans/import?year=${year}`, fd);
      toast.ok(`Загружено строк: ${res.imported}${res.errors.length ? `, с ошибками: ${res.errors.length}` : ""}`);
      m.reload();
    } catch (e) { toast.error(e); }
  };

  return (
    <>
      <TopBar title="План поставок">
        <Button className="icon-btn" aria-label="Предыдущий год" onClick={() => setQuery({ year: year - 1 })}>‹</Button>
        <b className="num">{year}</b>
        <Button className="icon-btn" aria-label="Следующий год" onClick={() => setQuery({ year: year + 1 })}>›</Button>
        {editable && <>
          <Button onClick={() => setAdding(true)}>Добавить строку</Button>
          <Button onClick={() => file.current?.click()}>Импорт из Excel</Button>
          <input ref={file} type="file" accept=".xlsx" hidden onChange={(e) => e.target.files?.[0] && importFile(e.target.files[0])} />
        </>}
      </TopBar>
      <div className="content">
        {!m.data ? <Loading error={m.error} onRetry={m.reload} /> : (
          <>
            <div className="kpis">
              <div className="kpi"><div className="k">План на год</div><div className="v">{fmtNum(m.data.year_plan, 0)} м³</div></div>
              <div className="kpi"><div className="k">Отгружено</div><div className="v">{fmtNum(m.data.year_fact, 0)} м³</div>
                <div className="s">{m.data.year_plan ? fmtNum((m.data.year_fact / m.data.year_plan) * 100, 0) + "%" : ""}</div></div>
            </div>
            <div className="panel table-wrap">
              <table className="table" style={{ minWidth: 1200 }}>
                <thead>
                  <tr><th style={{ minWidth: 220 }}>Клиент / объект</th>{M.map((x) => <th key={x} className="num">{x}</th>)}<th className="num">Год</th>{editable && <th />}</tr>
                </thead>
                <tbody>
                  {m.data.rows.map((row: any, i: number) => (
                    <tr key={i}>
                      <td>
                        <b>{row.customer_name}</b>{row.unplanned && <span className="tag plain" style={{ marginLeft: 6 }}>вне плана</span>}
                        {row.site_name && <div className="small muted">{row.site_name}{row.tower_cranes ? `, кранов: ${row.tower_cranes} (~${row.tower_cranes * 1000} м³/мес)` : ""}</div>}
                      </td>
                      {row.months.map((c: any) => (
                        <td key={c.month} className="num" style={{ cursor: editable ? "pointer" : "default", verticalAlign: "top" }}
                          onClick={() => editable && setEdit({ row, month: c.month })}
                          title={c.requested ? `Заявлено клиентом ${fmtNum(c.requested, 0)} м³` : undefined}>
                          <div>{c.plan ? fmtNum(c.plan, 0) : <span className="muted">·</span>}</div>
                          {c.requested ? <div className="small muted">заяв. {fmtNum(c.requested, 0)}</div> : null}
                          {c.fact > 0 && <div className="small" style={{ color: c.fact >= c.plan ? "var(--st-done)" : "var(--st-transit)" }}>факт {fmtNum(c.fact, 0)}</div>}
                        </td>
                      ))}
                      <td className="num"><b>{fmtNum(row.year_plan, 0)}</b><div className="small muted">факт {fmtNum(row.year_fact, 0)}</div></td>
                      {editable && <td>{!row.is_reserve && <Button size="small" variant="ghost" onClick={() => setShift(row)}>Сдвинуть</Button>}</td>}
                    </tr>
                  ))}
                  <tr>
                    <td><b>Итого</b></td>
                    {m.data.totals.map((t: any) => <td key={t.month} className="num"><b>{fmtNum(t.plan, 0)}</b><div className="small muted">{fmtNum(t.fact, 0)}</div></td>)}
                    <td className="num"><b>{fmtNum(m.data.year_plan, 0)}</b></td>
                    {editable && <td />}
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="muted small">Нажмите на месяц, чтобы изменить план. «Заявлено» — сколько обещал клиент; в план ставится реальный объём (например, по числу башенных кранов: около 1000 м³ в месяц на кран).</p>
          </>
        )}
      </div>
      {edit && <CellDialog year={year} row={edit.row} month={edit.month} onClose={() => setEdit(null)} onDone={() => { setEdit(null); m.reload(); }} />}
      {shift && <ShiftDialog year={year} row={shift} onClose={() => setShift(null)} onDone={() => { setShift(null); m.reload(); }} />}
      {adding && <AddRow year={year} onClose={() => setAdding(false)} onDone={() => { setAdding(false); m.reload(); }} />}
    </>
  );
}

function CellDialog({ year, row, month, onClose, onDone }: { year: number; row: any; month: number; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const cell = row.months[month - 1];
  const [plan, setPlan] = useState(String(cell.plan || ""));
  const [req, setReq] = useState(cell.requested ? String(cell.requested) : "");
  const [days, setDays] = useState<any[] | null>(null);
  return (
    <Modal title={`${row.customer_name}${row.site_name ? ", " + row.site_name : ""}: ${M[month - 1]} ${year}`} onClose={onClose} actions={<>
      <Button onClick={onClose}>Отмена</Button>
      <Button variant="primary" onClick={async () => {
        try {
          await api.put("/supply-plans", { customer_id: row.customer_id, site_id: row.site_id, year, month, volume_m3: Number(plan || 0),
            volume_requested_m3: req ? Number(req) : null, is_reserve: row.is_reserve });
          onDone();
        } catch (e) { toast.error(e); }
      }}>Сохранить</Button>
    </>}>
      <div className="form-grid">
        <Field label="Принято в план, м³"><Input inputMode="decimal" autoFocus value={plan} onChange={(e) => setPlan(e.target.value)} /></Field>
        <Field label="Заявлено клиентом, м³"><Input inputMode="decimal" value={req} onChange={(e) => setReq(e.target.value)} /></Field>
      </div>
      <Button size="small" variant="ghost" onClick={async () => setDays(await api.get("/supply-plans/distribute", { year, month, volume_m3: Number(plan || 0) }))}>
        Показать по дням (завод работает без выходных)
      </Button>
      {days && (
        <div className="small" style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
          {days.map((d) => <span key={d.date} className="num" style={{ opacity: d.volume_m3 ? 1 : 0.4 }}>{d.date.slice(8)}: {fmtNum(d.volume_m3, 0)}</span>)}
        </div>
      )}
    </Modal>
  );
}

function ShiftDialog({ year, row, onClose, onDone }: { year: number; row: any; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [from, setFrom] = useState(String(new Date().getMonth() + 1));
  const [months, setMonths] = useState("1");
  return (
    <Modal title={`Сдвинуть план: ${row.customer_name}${row.site_name ? ", " + row.site_name : ""}`} onClose={onClose} actions={<>
      <Button onClick={onClose}>Отмена</Button>
      <Button variant="primary" onClick={async () => {
        try {
          await api.post("/supply-plans/shift", { site_id: row.site_id, customer_id: row.site_id ? null : row.customer_id, year, from_month: Number(from), months: Number(months) });
          toast.ok("План сдвинут");
          onDone();
        } catch (e) { toast.error(e); }
      }}>Сдвинуть</Button>
    </>}>
      <p className="small muted">Например, на котловане встретили плывун — объект сдвигается на месяц вправо одной операцией.</p>
      <div className="form-grid">
        <Field label="Начиная с месяца"><Select value={from} onChange={(e) => setFrom(e.target.value)} options={M.map((x, i) => ({ value: String(i + 1), label: x }))} /></Field>
        <Field label="На сколько месяцев" hint="Отрицательное число — сдвиг влево"><Input inputMode="numeric" value={months} onChange={(e) => setMonths(e.target.value)} /></Field>
      </div>
    </Modal>
  );
}

function AddRow({ year, onClose, onDone }: { year: number; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const customers = useAsync<any>(() => api.get("/customers", { limit: 500 }), []);
  const [cid, setCid] = useState("");
  const sites = useAsync<any>(() => (cid ? api.get("/sites", { customer_id: cid }) : Promise.resolve({ items: [] })), [cid]);
  const [sid, setSid] = useState("");
  const [vol, setVol] = useState("");
  const [month, setMonth] = useState(String(new Date().getMonth() + 1));
  return (
    <Modal title="Строка плана" onClose={onClose} actions={<>
      <Button onClick={onClose}>Отмена</Button>
      <Button variant="primary" disabled={!cid || !vol} onClick={async () => {
        try { await api.put("/supply-plans", { customer_id: cid, site_id: sid || null, year, month: Number(month), volume_m3: Number(vol) }); onDone(); } catch (e) { toast.error(e); }
      }}>Добавить</Button>
    </>}>
      <Field label="Клиент"><Select value={cid} onChange={(e) => setCid(e.target.value)} placeholder="—" options={(customers.data?.items || []).map((c: any) => ({ value: c.id, label: c.name }))} /></Field>
      <Field label="Объект" hint="Можно оставить пустым — план на клиента целиком"><Select value={sid} onChange={(e) => setSid(e.target.value)} placeholder="—" options={(sites.data?.items || []).map((s: any) => ({ value: s.id, label: s.name }))} /></Field>
      <div className="form-grid">
        <Field label="Месяц"><Select value={month} onChange={(e) => setMonth(e.target.value)} options={M.map((x, i) => ({ value: String(i + 1), label: x }))} /></Field>
        <Field label="Объём, м³"><Input inputMode="decimal" value={vol} onChange={(e) => setVol(e.target.value)} /></Field>
      </div>
    </Modal>
  );
}
